"use client";

// =========================================================================
// TimelineEmailBody — inline, scrollable full email body in the timeline.
//
// WHY: for HTML emails the stored `body_preview` is the first ~280 chars of raw
// markup (usually just `<head><style>…`), which sanitizes to nothing — so the
// timeline bubble showed no body. The real body lives in S3 (COMMS_BODY_S3_AND_FTS).
//
// UX: the body loads automatically — no click-to-expand. Rows near the top of
// the timeline (`eager`) fetch on mount; the rest fetch as they scroll into the
// viewport (IntersectionObserver with a look-ahead margin), so we only ever pull
// from S3 what the user is actually about to read. A subtle collapse toggle lets
// a long email be hidden after it loads, but reading it never requires a click.
//
// The body lives in the shared ["communication-body", id] query, so a row that
// re-mounts (timeline re-render, scrolling back) reuses it instead of re-reading
// S3, and the detail modal for the same message shares the same entry.
// =========================================================================

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react";
import { useAuth } from "@west-tennessee-consulting/ui";
import { Button } from "@/components/ui/button";
import { SafeEmailBody } from "@/components/crm/messages/components/SafeEmailBody";
import { useCommunicationBodyQuery } from "@/lib/hooks/use-communication-body-query";

interface TimelineEmailBodyProps {
  communicationId: string;
  /** Preview markup already on the row — shown until the full body loads. */
  previewBody?: string;
  /** Fetch the full body immediately on mount (used for the top rows). */
  eager?: boolean;
}

export function TimelineEmailBody({
  communicationId,
  previewBody,
  eager = false,
}: TimelineEmailBodyProps) {
  const { getAuthToken } = useAuth();
  // Becomes true once the row should load (eager, or scrolled near view); from
  // then on the query owns fetching, dedupe and the cached result.
  const [scrolledIntoView, setScrolledIntoView] = useState(false);
  const requested = eager || scrolledIntoView;
  const [collapsed, setCollapsed] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);

  const bodyQuery = useCommunicationBodyQuery(communicationId, {
    enabled: requested,
    getAuthToken,
  });
  const { refetch } = bodyQuery;

  const loading = bodyQuery.isFetching;
  const error = !bodyQuery.isFetching && bodyQuery.error ? bodyQuery.error.message : null;
  const fullBody = bodyQuery.data !== undefined ? (bodyQuery.data ?? "") : null;

  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);

  // Auto-load: eager rows on mount; the rest when they scroll near the viewport.
  useEffect(() => {
    if (requested) return;
    const el = containerRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      // No observer support (SSR / jsdom) — fall back to loading immediately.
      setScrolledIntoView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setScrolledIntoView(true);
          observer.disconnect();
        }
      },
      { rootMargin: "300px 0px" }, // begin fetching just before the row is visible
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [requested]);

  const toggleCollapsed = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setCollapsed((prev) => !prev);
  }, []);

  return (
    <div ref={containerRef} onClick={(e) => e.stopPropagation()}>
      {loading && (
        <div className="flex items-center gap-2 text-xs text-muted-foreground px-1 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Loading email…
        </div>
      )}

      {error && (
        <div className="flex items-center justify-between gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
          <span className="text-xs text-destructive">{error}</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              retry();
            }}
          >
            Retry
          </Button>
        </div>
      )}

      {!loading && !error && fullBody !== null && (
        <>
          {!collapsed && (
            <div className="max-h-96 overflow-y-auto rounded-md">
              <SafeEmailBody
                body={fullBody || previewBody || ""}
                variant="full"
                collapseQuotedReplies
                stopLinkClickPropagation
              />
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="text-xs text-muted-foreground -ml-2 mt-1"
            onClick={toggleCollapsed}
            aria-expanded={!collapsed}
          >
            {collapsed ? (
              <ChevronDown className="h-3.5 w-3.5 mr-1" />
            ) : (
              <ChevronUp className="h-3.5 w-3.5 mr-1" />
            )}
            {collapsed ? "Show email" : "Hide email"}
          </Button>
        </>
      )}
    </div>
  );
}
